import { apiGet, apiSend, apiSendWithHeaders, apiUpload, forgetCsrfToken } from './client'
import type {
  AccountMe,
  AdminProduct,
  AdminSetting,
  ReportedPhoto,
  TwoFactorSetup,
  TwoFactorStatus,
  ConnectedContacts,
  ConnectionState,
  Invitation,
  InviteeView,
  MatchHistoryEntry,
  MatchReport,
  MatchingCount,
  Order,
  PaymentSession,
  UnlockNext,
  CompletionCheck,
  CompletionResult,
  LocationItem,
  MyPhoto,
  PreferenceReferenceCatalogue,
  Preferences,
  Profile,
  ProfileReferenceCatalogue,
  ProfileUpdate,
  UpdateOutcome,
} from './types'

/** Every route the interface calls, in one list, so a rename shows up in one diff. */
export const api = {
  reference: {
    profile: () => apiGet<ProfileReferenceCatalogue>('/api/reference/profile'),
    preferences: () => apiGet<PreferenceReferenceCatalogue>('/api/reference/preferences'),
    locations: (parentId?: string) =>
      apiGet<{ items: LocationItem[] }>(
        parentId ? `/api/reference/locations?parentId=${parentId}` : '/api/reference/locations',
      ).then((body) => body.items),
  },

  auth: {
    register: (email: string, password: string) =>
      apiSend<{ userId: string; email: string }>('POST', '/api/auth/register', { email, password }),

    /**
     * Resolves to true when the password was right and a second factor is still owed
     * (安全架构 §21). The API answers 204 for a completed sign-in — the shape 契约 §4.2 froze —
     * and 200 with a body only in the new case, so a client that only knows about 204 is still
     * correct.
     */
    login: async (email: string, password: string, rememberMe: boolean) => {
      const body = await apiSend<{ twoFactorRequired: boolean } | undefined>(
        'POST', '/api/auth/login', { email, password, rememberMe })

      // Signing in rotates the session, and the token was issued against the old one.
      forgetCsrfToken()

      return body?.twoFactorRequired === true
    },

    twoFactor: async (code: string) => {
      await apiSend<void>('POST', '/api/auth/two-factor', { code })
      forgetCsrfToken()
    },

    logout: async () => {
      await apiSend<void>('POST', '/api/auth/logout')
      forgetCsrfToken()
    },

    verifyEmail: (userId: string, token: string) =>
      apiSend<void>('POST', '/api/auth/verify-email', { userId, token }),

    resendVerification: (email: string) =>
      apiSend<void>('POST', '/api/auth/resend-verification', { email }),

    forgotPassword: (email: string) =>
      apiSend<void>('POST', '/api/auth/forgot-password', { email }),

    resetPassword: (email: string, token: string, newPassword: string) =>
      apiSend<void>('POST', '/api/auth/reset-password', { email, token, newPassword }),
  },

  account: {
    me: () => apiGet<AccountMe>('/api/account/me'),
  },

  profile: {
    get: () => apiGet<Profile>('/api/profile/me'),
    update: (body: ProfileUpdate) => apiSend<UpdateOutcome>('PUT', '/api/profile/me', body),
    citizenships: (countryIds: string[]) =>
      apiSend<UpdateOutcome>('PUT', '/api/profile/me/citizenships', { countryIds }),
    languages: (languages: { languageId: string; proficiencyId?: string | null }[]) =>
      apiSend<UpdateOutcome>('PUT', '/api/profile/me/languages', { languages }),
    completion: () => apiGet<CompletionCheck>('/api/profile/me/completion'),
    complete: (version: number) =>
      apiSend<CompletionResult>('POST', '/api/profile/me/complete', { version }),
  },

  photo: {
    mine: () => apiGet<MyPhoto>('/api/profile/me/photo'),

    upload: (file: File) => {
      const form = new FormData()
      form.append('file', file)

      return apiUpload<MyPhoto>('/api/profile/me/photo', form)
    },

    remove: () => apiSend<void>('DELETE', '/api/profile/me/photo'),

    /** The bytes come from here, and only for someone entitled to them (Group 20). */
    url: (photoId: string) => `/api/photos/${photoId}`,
  },

  matching: {
    count: () => apiGet<MatchingCount>('/api/matching/count'),

    /**
     * Freezes the next candidate. Takes no id on purpose: what is bought is a position in the
     * queue, not a person — the queue is recomputed when the button is pressed, and L1 never
     * showed who was there (契约 §10.1).
     */
    unlockNext: () => apiSend<UnlockNext>('POST', '/api/unlocks/next'),

    history: () => apiGet<{ items: MatchHistoryEntry[] }>('/api/matches').then((b) => b.items),
    report: (matchId: string) => apiGet<MatchReport>(`/api/matches/${matchId}/report`),
  },

  orders: {
    /**
     * The idempotency key is the client's, because only the client knows that two requests are
     * the same attempt (匹配算法 §18). A retry has to reach the same order, not a second one.
     */
    create: (matchId: string, productCode: string, idempotencyKey: string) =>
      apiSendWithHeaders<Order>('POST', '/api/orders', { matchId, productCode }, {
        'Idempotency-Key': idempotencyKey,
      }),

    get: (orderId: string) => apiGet<Order>(`/api/orders/${orderId}`),

    paymentSession: (orderId: string) =>
      apiSend<PaymentSession>('POST', `/api/orders/${orderId}/payment-session`),
  },

  invitations: {
    send: (matchId: string, message: string | null) =>
      apiSend<Invitation>('POST', '/api/invitations', { matchId, message }),

    reInvite: (previousMatchId: string, message: string | null) =>
      apiSend<Invitation>('POST', '/api/invitations/re-invite', { previousMatchId, message }),

    incoming: () => apiGet<{ items: Invitation[] }>('/api/invitations/incoming').then((b) => b.items),
    outgoing: () => apiGet<{ items: Invitation[] }>('/api/invitations/outgoing').then((b) => b.items),

    view: (invitationId: string) => apiGet<InviteeView>(`/api/invitations/${invitationId}/view`),

    cancel: (invitationId: string) =>
      apiSend<Invitation>('POST', `/api/invitations/${invitationId}/cancel`),

    decline: (invitationId: string) =>
      apiSend<Invitation>('POST', `/api/invitations/${invitationId}/decline`),

    accept: (invitationId: string) =>
      apiSend<unknown>('POST', `/api/invitations/${invitationId}/accept`),
  },

  connection: {
    current: () => apiGet<ConnectionState | null>('/api/connections/current'),
    contacts: () => apiGet<ConnectedContacts>('/api/connections/current/contacts'),
    close: (connectionId: string, reason: string | null) =>
      apiSend<unknown>('POST', `/api/connections/${connectionId}/close`, { reason }),
  },

  admin: {
    twoFactorStatus: () => apiGet<TwoFactorStatus>('/api/admin/two-factor'),
    twoFactorSetup: () => apiSend<TwoFactorSetup>('POST', '/api/admin/two-factor/setup'),
    twoFactorEnable: (code: string) =>
      apiSend<{ recoveryCodes: string[] }>('POST', '/api/admin/two-factor/enable', { code }),

    products: () => apiGet<AdminProduct[]>('/api/admin/products'),
    changePrice: (code: string, newPrice: number) =>
      apiSend<void>('PUT', `/api/admin/products/${code}/price`, { newPrice }),

    settings: () => apiGet<AdminSetting[]>('/api/admin/settings'),
    updateSetting: (key: string, value: string) =>
      apiSend<void>('PUT', `/api/admin/settings/${encodeURIComponent(key)}`, { value }),

    photoReports: () => apiGet<ReportedPhoto[]>('/api/admin/photos/reports'),
    photoTakedown: (photoId: string) =>
      apiSend<void>('POST', `/api/admin/photos/${photoId}/takedown`),
    photoRestore: (photoId: string) =>
      apiSend<void>('POST', `/api/admin/photos/${photoId}/restore`),
    photoNotify: (photoId: string) =>
      apiSend<void>('POST', `/api/admin/photos/${photoId}/notify`),
  },

  preferences: {
    get: () => apiGet<Preferences>('/api/preferences/me'),
    update: (body: unknown) => apiSend<UpdateOutcome>('PUT', '/api/preferences/me', body),
    completion: () => apiGet<CompletionCheck>('/api/preferences/me/completion'),
    complete: (version: number) =>
      apiSend<CompletionResult>('POST', '/api/preferences/me/complete', { version }),
  },
}
